/**
 * OverflowMenu — renders toolbar actions that don't fit into a "⋯" dropdown.
 * Uses a portal to escape stacking contexts, same pattern as FilterBar dropdowns.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface OverflowMenuItem {
  readonly id: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly active?: boolean;
}

interface OverflowMenuProps {
  readonly items: OverflowMenuItem[];
  readonly buttonClassName: string;
}

export function OverflowMenu({ items, buttonClassName }: OverflowMenuProps): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open, updatePosition]);

  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="More actions"
        aria-expanded={open}
        aria-haspopup="menu"
        className={buttonClassName}
      >
        ⋯
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="More actions"
          className="fixed z-[9999] min-w-[10rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          <div className="p-1">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                onClick={() => { item.onClick(); setOpen(false); }}
                className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 px-3 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              >
                {item.active && <span className="mr-2 text-primary">●</span>}
                {item.label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
